// Self-hosted client-side error reporting.
//
// We installed this instead of Sentry because the team is at an early stage
// and Sentry's free tier signup pushes you to a trial. This routes any
// JS runtime error to our own /api/log/error endpoint, which stores it in
// MongoDB and surfaces it in the admin dashboard. Sampled at 100% — at
// our current scale that's fine; if we hit 5k errors/day we'll switch.

import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${BACKEND}/api/log/error`;
// Frontend version — bumped on each release via package.json. Reading
// process.env so CRA inlines it at build time.
const APP_VERSION = process.env.REACT_APP_VERSION || "unknown";

// Don't fire ourselves into an infinite loop if our own logger crashes,
// and don't spam the server with the same error 100x/sec on a runaway
// render loop.
let inFlight = false;
const recent = new Map(); // message -> timestamp of last send
const DEDUPE_WINDOW_MS = 60_000; // one report per unique message per minute
const MAX_RECENT = 50;

// ─── Third-party noise filter ──────────────────────────────────────────────
// Errors that originate inside scripts we don't own — Cloudflare Insights,
// analytics beacons, browser extensions, etc. — are not actionable for us.
// We filter them out here so they don't spam the on-call inbox.
//
// Match against the error's originating filename AND the stack trace text,
// because different browsers surface each differently.
const THIRD_PARTY_SIGNATURES = [
  "cloudflareinsights.com",       // Cloudflare Insights beacon (auto-injected)
  "cloudflare-static/",           // CF static assets
  "googletagmanager.com",         // GTM
  "google-analytics.com",         // GA
  "chrome-extension://",          // Chrome extensions
  "moz-extension://",             // Firefox extensions
  "safari-extension://",          // Safari extensions
  "safari-web-extension://",      // Safari web extensions
  "webkit-masked-url",            // Safari-injected extension URLs
  "extensions/",                  // generic extension paths
];

// Message patterns that are almost always browser noise, not real bugs.
const NOISY_MESSAGE_PATTERNS = [
  /ResizeObserver loop /i,                    // benign layout-thrash warning
  /Script error\.?$/,                          // cross-origin script errors — no stack, no context
  /Non-Error promise rejection captured/i,
  /Load failed$/,                              // iOS Safari opaque network fail
  /\bNetwork Error\b/i,                        // axios's generic offline / connection-drop signal — user-context, not our bug
  /timeout of \d+ms exceeded/i,                // axios request-timeout — same class of transient noise
  /ERR_NETWORK/,                               // axios v1+ code for network failures
  /ERR_INTERNET_DISCONNECTED/,                 // Chromium offline signal
  /ERR_CONNECTION_(CLOSED|RESET|REFUSED)/,     // TCP-level connection failures user-side
  /The operation couldn.?t be completed.*(offline|network|internet)/i,  // iOS Safari localized network errors
  // Aborted / cancelled in-flight requests — happens when a component
  // unmounts before the network call resolves (user navigates away).
  // Never a real bug; always noise.
  /\bRequest aborted\b/i,
  /\b(canceled|cancelled)\b/i,
  /\bERR_CANCELED\b/,
  // Expected business-logic HTTP status codes — handled downstream so
  // they should never trigger an alert:
  //   401 → session expired, user is redirected to /sign-in
  //   402 → subscription required, user is redirected to /pricing (see GlobalPaywallGuard)
  //   429 → rate-limited, upstream handles the retry
  /Request failed with status code (401|402|429)\b/,
  // Browser-extension chatter that bubbles up to window.unhandledrejection.
  // Users have all kinds of Safari/Chrome extensions installed
  // (1Password, LastPass, content blockers, VPN extensions) that fire
  // `runtime.sendMessage` at tabs that have already closed — the extension
  // is talking to itself, not to us, and there's nothing we can fix.
  /runtime\.sendMessage/i,
  /Tab not found/i,
  /Extension context invalidated/i,
  /Could not establish connection\. Receiving end does not exist/i,
  /Extension port(?:.*?)disconnected/i,
  // Mobile network hiccups — Chrome/Safari throw "Failed to fetch" any time
  // the phone briefly loses connectivity (WiFi ↔ cellular handoff, elevator,
  // subway, poor signal). These are pretested with [handled] because our
  // code already caught the exception and continued — no user impact and no
  // action Sarah can take. Only filters when marked [handled] so a genuine
  // uncaught fetch-fail still emails.
  /\[handled\][^]*Failed to fetch/i,
  /\[handled\][^]*NetworkError/i,
  /\[handled\][^]*Load failed/i,
];

const isThirdPartyNoise = ({ message, stack, filename }) => {
  const haystack = `${filename || ""}\n${stack || ""}`;
  if (THIRD_PARTY_SIGNATURES.some((sig) => haystack.includes(sig))) return true;
  if (message && NOISY_MESSAGE_PATTERNS.some((rx) => rx.test(message))) return true;
  return false;
};

const report = (message, stack, filename) => {
  try {
    if (!message) return;
    if (isThirdPartyNoise({ message, stack, filename })) return;
    if (inFlight) return;
    const now = Date.now();
    const lastSeen = recent.get(message);
    if (lastSeen && now - lastSeen < DEDUPE_WINDOW_MS) return;
    recent.set(message, now);
    // Cap memory.
    if (recent.size > MAX_RECENT) {
      const oldestKey = recent.keys().next().value;
      recent.delete(oldestKey);
    }
    inFlight = true;
    axios.post(
      ENDPOINT,
      {
        message: String(message).slice(0, 2048),
        path: window.location?.pathname || "",
        stack: stack ? String(stack).slice(0, 4096) : null,
        user_agent: navigator.userAgent || "",
        app_version: APP_VERSION,
      },
      // Don't carry credentials — public endpoint, simpler CORS handling
      // (same wildcard-Allow-Origin / credentials clash that bit us on iOS).
      { withCredentials: false, timeout: 5000 }
    ).catch(() => { /* ignore — logging must never escalate */ })
      .finally(() => { inFlight = false; });
  } catch (_) {
    inFlight = false;
    // never let the logger throw
  }
};

export const installErrorReporting = () => {
  // 1. window.onerror — synchronous and uncaught exceptions.
  window.addEventListener("error", (e) => {
    report(e.message || "Unknown window error", e.error?.stack, e.filename);
  });

  // 2. Unhandled promise rejections — usually async / fetch failures.
  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason;
    if (!reason) return;
    const msg =
      reason?.message ||
      reason?.toString?.() ||
      "Unhandled promise rejection";
    // Filter out the noisy axios "Network Error" cases on iOS — they
    // surface elsewhere with better context.
    if (msg === "Network Error") return;
    report(`unhandledrejection: ${msg}`, reason?.stack);
  });
};

// Manual report — call this from catch blocks where you want explicit
// signal. e.g., logger.error("Apple sign-in failed", err) becomes
// also reportClientError(err.message, err.stack)
export const reportClientError = (message, stack) => report(message, stack);
